import React, { useMemo } from 'react';
import { setSelectedAvailablePack, setSelectedReplacedPack, setSelectedSegment } from './formSlice';
import { RootState } from '../../state/store';
import {
  AvailablePacksContainer,
  AvailableSegment,
  DeselectIcon,
  Segment,
  SegmentContainer,
  SmallTypography,
} from '../../pages/ScenarioCreation/components/SegmentSelection/DynamicContents.Styled';
import { Grid, Skeleton } from '@mui/material';
import { useAppDispatch, useAppSelector } from '../../state/hooks';
import { fetchBrands } from '../../features/brand/brandSlice';
import {
  fetchSkuSizeReplacementPacks,
  fetchStateWithPackSegment,
  resetStatesWithAvailablePackSegments,
  setIsPackReplacable,
} from '../../features/packSegments/packSegmentsSlice';
import {
  fetchKpiCalculation,
  fetchLaunchKpiCalculation,
  resetInputKpiState,
} from '../../features/inputKpi/inputKpiSlice';
import { toggleEnableReviewTableBtn } from '../../pages/ScenarioCreation/components/ReviewTable/reviewTableSlice';
import { hideLoader, showLoader } from '../../features/ui/loaderSlice';
import { ReplaceIcon } from '../../assets/images/UI-dashboard';

const PackSegments: React.FC<{ data: any }> = ({ data }) => {
  const dispatch = useAppDispatch();
  const { selectedSegment, selectedAvailablePack, selectedReplacedPack } = useAppSelector(
    (state: RootState) => state.form,
  );
  const { isPackReplacable, replacablePackSizes } = useAppSelector((state: RootState) => state.segment);
  const { current_tab } = useAppSelector((state: RootState) => state.inputKpi);
  const { selectedBrands } = useAppSelector((state: RootState) => state.brand);
  const filtersState = useAppSelector((state) => state.filters);

  const handleSelectSegment = async (segment: string) => {
    await dispatch(resetInputKpiState());
    // await dispatch(toggleEnableReviewTableBtn(false));
    await dispatch(setSelectedReplacedPack(null));
    await dispatch(setIsPackReplacable(false));
    if (segment === selectedSegment) {
      await dispatch(setSelectedSegment(''));
      await dispatch(resetStatesWithAvailablePackSegments());
    } else {
      await dispatch(showLoader());
      await dispatch(setSelectedSegment(segment));
      const payload = {
        category: filtersState.enabledKeys.category,
        pack_segment: [`${segment}`],
      };
      await dispatch(fetchStateWithPackSegment(payload));
      await dispatch(hideLoader());
    }
  };

  const handleSelectPack = async (pack: number) => {
    if (pack === selectedAvailablePack) {
      await dispatch(setSelectedAvailablePack(null));
      await dispatch(resetInputKpiState());
      // await dispatch(toggleEnableReviewTableBtn(false));
    } else {
      await dispatch(showLoader());
      await dispatch(setSelectedAvailablePack(pack));
      const payload = {
        bottler: filtersState.enabledKeys.bottler,
        state: filtersState.enabledKeys.state,
        category: filtersState.enabledKeys.category,
        pack_segment: [`${selectedSegment}`],
      };
      const response = await dispatch(fetchBrands({ ...payload, pack_size: [`${pack}`] })).unwrap();
      if (current_tab === 'Replace') {
        const brands: any[] = [];

        Object.keys(response).forEach((ele) => {
          brands.push(ele);
        });

        const payloadForReplacementPacks = {
          ...payload,
          brand: brands,
          available_sku_size: [pack],
        };

        await dispatch(fetchSkuSizeReplacementPacks(payloadForReplacementPacks));
        await dispatch(hideLoader());
      }
      if (current_tab === 'Launch') {
        await dispatch(
          fetchLaunchKpiCalculation({
            ...payload,
            pack_size: [`${pack}`],
            intervention_type: 'Launch',
          }),
        );
        await dispatch(hideLoader());
      }
    }
  };

  const handleReplacementPack = async (val: number) => {
    await dispatch(showLoader());
    if (val === selectedReplacedPack) {
      await dispatch(setSelectedReplacedPack(null));
    } else {
      await dispatch(setSelectedReplacedPack(val));
      const payload = {
        bottler: filtersState.enabledKeys.bottler,
        state: filtersState.enabledKeys.state,
        category: filtersState.enabledKeys.category,
        brand: selectedBrands,
        pack_segment: [`${selectedSegment}`],
        pack_size: [`${selectedAvailablePack}`],
        pack_size_replace: [`${val}`],
      };
      if (current_tab === 'Replace') {
        await dispatch(
          fetchKpiCalculation({
            ...payload,
            intervention_type: 'Replace',
          }),
        );
      }
    }
    await dispatch(hideLoader());
  };

  const uniqueValues = [...new Set(replacablePackSizes['new_sku_size'])];

  const sortedSegmentData = useMemo(() => {
    if (!selectedSegment || !Array.isArray(data[0][selectedSegment])) {
      return [];
    }

    return [...data[0][selectedSegment]].sort((a, b) => (a > b ? 1 : -1));
  }, [selectedSegment, data]);

  return (
    <Grid
      style={{
        display: 'grid',
        gridTemplateColumns: '60% 40%',
        marginTop: '5px',
      }}
    >
      <SegmentContainer>
        <SmallTypography>Pack Segments</SmallTypography>
        <Grid style={{ display: 'flex', flexWrap: 'wrap' }}>
          {false ? (
            <Grid style={{ display: 'flex' }}>
              {['1', '2', '3'].map(() => (
                <Skeleton style={{ marginRight: '10px' }} variant="rectangular" width={60} height={30} />
              ))}
            </Grid>
          ) : (
            Object.keys(data[0]).map((text, index) => (
              <Segment key={index} selected={text === selectedSegment} onClick={() => handleSelectSegment(text)}>
                {text}
                {text === selectedSegment && (
                  <DeselectIcon onClick={() => dispatch(setSelectedSegment(''))}>&times;</DeselectIcon>
                )}
              </Segment>
            ))
          )}
        </Grid>
      </SegmentContainer>
      {!isPackReplacable && (
        <AvailablePacksContainer style={{ marginLeft: '10px' }}>
          <SmallTypography>Available Packs</SmallTypography>
          {false ? (
            <Grid>
              <Skeleton variant="rectangular" width={250} height={30} />
            </Grid>
          ) : (
            <Grid
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                flexDirection: 'row',
              }}
            >
              {sortedSegmentData
                ?.sort((a, b) => (a > b ? 1 : -1))
                .map((packValue: any, index: any) => (
                  <AvailableSegment
                    style={{
                      border: '1px solid #f5f5f5',
                    }}
                    key={index}
                    selected={packValue === selectedAvailablePack}
                    onClick={() => handleSelectPack(packValue)}
                  >
                    {packValue}
                  </AvailableSegment>
                ))}
            </Grid>
          )}
        </AvailablePacksContainer>
      )}
      {isPackReplacable && (
        <AvailablePacksContainer>
          <SmallTypography>
            Selected <SmallTypography style={{ marginLeft: '25px' }}>Replace with</SmallTypography>
          </SmallTypography>

          <Grid
            style={{
              display: 'grid',
              gridTemplateColumns: '20% 10% 70%',
            }}
          >
            <Grid>
              <AvailableSegment
                style={{
                  border: '1px solid red',
                  height: '21px',
                  borderRadius: '7px',
                  display: 'flex',
                  justifyContent: 'center',
                  alignContent: 'center',
                  alignItems: 'center',
                }}
                key={0}
                selected={true}
              >
                {selectedAvailablePack}
              </AvailableSegment>
            </Grid>
            <img
              style={{
                marginLeft: '5px',
                marginRight: '5px',
                marginTop: '5px',
              }}
              src={ReplaceIcon}
              alt="replace-icon"
            />
            {uniqueValues.length === 0 ? (
              <Grid style={{ display: 'flex' }}>
                {['1', '2', '3'].map(() => (
                  <Skeleton style={{ marginRight: '10px' }} variant="rectangular" width={40} height={20} />
                ))}
              </Grid>
            ) : (
              <Grid style={{ display: 'flex', flexFlow: 'wrap' }}>
                {uniqueValues
                  .sort((a, b) => (a > b ? 1 : -1))
                  .map((ele: any, index: any) => (
                    <AvailableSegment
                      style={{
                        border: '1px solid #f5f5f5',
                      }}
                      key={index}
                      selected={ele === selectedReplacedPack}
                      onClick={() => handleReplacementPack(ele)}
                    >
                      {ele}
                    </AvailableSegment>
                  ))}
              </Grid>
            )}
          </Grid>
        </AvailablePacksContainer>
      )}
    </Grid>
  );
};

export default PackSegments;
